import React, { FC, useRef, useState } from 'react'
import classNames from 'classnames'
import useFormData from 'hooks/formData'
import { useFlashMessage } from 'hooks/useFlashMessage'
import Select, { SelectOption } from 'components/SA/Select'
import InputLabel from 'components/SA/FormElements/InputLabel'
import TextAreaLabel from 'components/SA/FormElements/TextareaLabel'
import { submitSupportTicket } from 'apis/private/support'
import { TReason, TInitialState } from './types'
import { reasonOptions, getReasonFlags, ERROR_MESSAGES, FORM_FIELDS } from './constants'
import styles from './RequestSupportModal.module.scss'

type Props = {
  userId: number
  email: string
  names: { firstName: string; lastName: string }
  onClose: () => void
}

const initialState: TInitialState = {
  reason: '',
  artworkTitle: '',
  orderNumber: '',
  phoneNumber: '',
  description: '',
}

const RequestSupportForm: FC<Props> = ({
  userId,
  email,
  names: { firstName, lastName },
  onClose,
}) => {
  const { showErrorMessage, showSuccessMessage } = useFlashMessage()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const screenshotRef = useRef<HTMLInputElement>(null)
  const { formData, formErrors, onChangeInput, updateFormData, updateFormErrors } = useFormData({
    initialState,
  })

  const { reason, description, artworkTitle, orderNumber, phoneNumber } = formData

  const { isOrderOrPayout, isTechnical } = getReasonFlags(reason)

  const selectedReasonOption = reasonOptions.find((option) => option.value === reason)

  const handleReasonChange = (option: SelectOption<string>) => {
    updateFormData({
      reason: option.value as TReason,
      artworkTitle: '',
      orderNumber: '',
      phoneNumber: '',
    })
    if (screenshotRef.current) screenshotRef.current.value = ''
    updateFormErrors({})
  }

  const handleScreenshotChange = () => {
    if (formErrors.screenshot) updateFormErrors({ ...formErrors, screenshot: '' })
  }

  const validate = (): boolean => {
    const errors: Record<string, string> = {}

    if (!reason) errors.reason = ERROR_MESSAGES.REASON_REQUIRED
    if (!description.trim()) errors.description = ERROR_MESSAGES.FIELD_REQUIRED
    if (
      isTechnical &&
      (!screenshotRef.current?.files || screenshotRef.current.files.length === 0)
    ) {
      errors.screenshot = ERROR_MESSAGES.SCREENSHOT_REQUIRED
    }
    if (isOrderOrPayout) {
      if (!artworkTitle.trim()) errors.artworkTitle = ERROR_MESSAGES.FIELD_REQUIRED
      if (!orderNumber.trim()) errors.orderNumber = ERROR_MESSAGES.FIELD_REQUIRED
      if (!phoneNumber.trim()) errors.phoneNumber = ERROR_MESSAGES.FIELD_REQUIRED
    }

    updateFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const onSubmit = async () => {
    if (isSubmitting) return
    if (!validate()) return

    setIsSubmitting(true)

    const data = new FormData()
    data.append(FORM_FIELDS.ARTIST_ID, String(userId))
    data.append(FORM_FIELDS.FIRST_NAME, firstName)
    data.append(FORM_FIELDS.LAST_NAME, lastName)
    data.append(FORM_FIELDS.EMAIL, email)
    data.append(FORM_FIELDS.REASON, reason)
    data.append(FORM_FIELDS.DESCRIPTION, description)

    if (isTechnical && screenshotRef.current?.files?.[0]) {
      data.append(FORM_FIELDS.SCREENSHOT, screenshotRef.current.files[0])
    }
    if (isOrderOrPayout) {
      data.append(FORM_FIELDS.ARTWORK_TITLE, artworkTitle)
      data.append(FORM_FIELDS.ORDER_CONFIRMATION_REFERENCE, orderNumber)
      data.append(FORM_FIELDS.PHONE_NUMBER, phoneNumber)
    }

    const { success } = await submitSupportTicket(data)
    setIsSubmitting(false)

    if (success) {
      showSuccessMessage(
        'Your support request has been submitted. We will get back to you shortly.'
      )
      onClose()
    } else {
      showErrorMessage('Sorry, we could not submit your request. Please try again.')
    }
  }

  return (
    <>
      <h2 className={styles.title}>Request Support</h2>

      <Select
        placeholder='Select a reason'
        value={selectedReasonOption}
        onChange={handleReasonChange}
        options={reasonOptions}
        error={formErrors.reason}
        fullWidth
      />

      {isOrderOrPayout && (
        <>
          <InputLabel
            name='artworkTitle'
            value={artworkTitle}
            title='Artwork Title'
            onChange={onChangeInput}
            error={formErrors.artworkTitle}
          />
          <InputLabel
            name='orderNumber'
            value={orderNumber}
            title='Order ID'
            onChange={onChangeInput}
            error={formErrors.orderNumber}
          />
          <InputLabel
            name='phoneNumber'
            value={phoneNumber}
            title='Phone Number'
            type='tel'
            onChange={onChangeInput}
            error={formErrors.phoneNumber}
          />
        </>
      )}

      <TextAreaLabel
        name='description'
        value={description}
        title='Description'
        onChange={onChangeInput}
        error={formErrors.description}
      />

      {isTechnical && (
        <div className={styles.fileField}>
          <label className={styles.fileLabel}>
            Screenshot (required)
            <input
              ref={screenshotRef}
              type='file'
              accept='image/*'
              className={styles.fileInput}
              onChange={handleScreenshotChange}
            />
          </label>
          {formErrors.screenshot && (
            <span className='defaultInputError' data-type='error'>
              {formErrors.screenshot}
            </span>
          )}
        </div>
      )}

      <button
        type='button'
        className={classNames('defaultButton', { defaultButtonDisabled: isSubmitting })}
        onClick={onSubmit}
      >
        {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
      </button>
    </>
  )
}

export default RequestSupportForm
